import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Modal from "../Modal";
import { useWalkthrough } from "./WalkthroughContext";
import "./WalkthroughOverlay.css";

const PADDING = 8;
const BUBBLE_GAP = 18;
const BUBBLE_WIDTH = 300;

type Rect = { top: number; left: number; width: number; height: number };

const getRect = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const Typewriter: React.FC<{ text: string; speed?: number }> = ({
  text,
  speed = 22,
}) => {
  const [shown, setShown] = useState("");
  useEffect(() => {
    setShown("");
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        window.clearInterval(id);
      }
    }, speed);
    return () => window.clearInterval(id);
  }, [text, speed]);
  return (
    <span className="walkthrough-typewriter">
      {shown}
      <span className="walkthrough-cursor" aria-hidden="true" />
    </span>
  );
};

const WalkthroughOverlay: React.FC = () => {
  const { active, step, end, next, isIdle } = useWalkthrough();
  const [anchorRect, setAnchorRect] = useState<Rect | null>(null);
  const [highlightRect, setHighlightRect] = useState<Rect | null>(null);
  const [bubbleAnchorRect, setBubbleAnchorRect] = useState<Rect | null>(null);
  const anchorElRef = useRef<Element | null>(null);
  const highlightElRef = useRef<Element | null>(null);
  const bubbleAnchorElRef = useRef<Element | null>(null);

  useLayoutEffect(() => {
    if (!active || !step || !step.anchor) {
      setAnchorRect(null);
      setHighlightRect(null);
      anchorElRef.current = null;
      highlightElRef.current = null;
      return;
    }

    let raf = 0;
    let cancelled = false;
    const highlightSel = step.highlightAnchor || step.anchor;

    const resolve = () => {
      if (cancelled) return;
      const foundA = document.querySelector(step.anchor!);
      const foundH = highlightSel ? document.querySelector(highlightSel) : null;
      const foundB = step.bubbleAnchor ? document.querySelector(step.bubbleAnchor) : null;

      if (foundA) {
        anchorElRef.current = foundA;
        setAnchorRect(getRect(foundA));
      } else {
        anchorElRef.current = null;
        setAnchorRect(null);
      }
      if (foundH) {
        highlightElRef.current = foundH;
        setHighlightRect(getRect(foundH));
      } else {
        highlightElRef.current = null;
        setHighlightRect(null);
      }
      if (foundB) {
        bubbleAnchorElRef.current = foundB;
        setBubbleAnchorRect(getRect(foundB));
      } else {
        bubbleAnchorElRef.current = null;
        setBubbleAnchorRect(null);
      }
      if (!foundA || (highlightSel && !foundH) || (step.bubbleAnchor && !foundB)) {
        raf = window.requestAnimationFrame(resolve);
      }
    };

    resolve();

    const handleReposition = () => {
      const aEl = anchorElRef.current;
      if (aEl && aEl.isConnected) {
        setAnchorRect(getRect(aEl));
      } else if (step.anchor) {
        const foundA = document.querySelector(step.anchor);
        if (foundA) {
          anchorElRef.current = foundA;
          setAnchorRect(getRect(foundA));
        }
      }
      const hEl = highlightElRef.current;
      if (hEl && hEl.isConnected) {
        setHighlightRect(getRect(hEl));
      } else if (highlightSel) {
        const foundH = document.querySelector(highlightSel);
        if (foundH) {
          highlightElRef.current = foundH;
          setHighlightRect(getRect(foundH));
        }
      }
      const bEl = bubbleAnchorElRef.current;
      if (bEl && bEl.isConnected) {
        setBubbleAnchorRect(getRect(bEl));
      } else if (step.bubbleAnchor) {
        const foundB = document.querySelector(step.bubbleAnchor);
        if (foundB) {
          bubbleAnchorElRef.current = foundB;
          setBubbleAnchorRect(getRect(foundB));
        }
      }
    };

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    const observer = new MutationObserver(handleReposition);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const interval = window.setInterval(handleReposition, 250);

    return () => {
      cancelled = true;
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      observer.disconnect();
      window.clearInterval(interval);
    };
  }, [active, step]);

  // Tracks whether we're in the post-click fade-out window for a delayed
  // click-anchor advance. While true, we suppress the dim overlay, bubble, and
  // dot so the user can see what they just revealed (e.g. an expanded row).
  const [isFadingOut, setIsFadingOut] = useState(false);

  // click-anchor advance — supports clickAnchor override
  useEffect(() => {
    setIsFadingOut(false);
    if (!active || !step) return;
    if (step.advance.type !== "click-anchor") return;
    if (!step.anchor) return;

    // Use clickAnchor if provided, otherwise fall back to anchor
    const clickSel = step.clickAnchor || step.anchor;

    const handler = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      // Ignore clicks on the walkthrough UI itself (bubble, dot, idle toast)
      if (target.closest(".walkthrough-root")) return;
      // Match against the click selector or its first part (tag match)
      const groupSelector = clickSel.split(/\s/)[0];
      const matches =
        target.closest(clickSel) ||
        (groupSelector ? target.closest(groupSelector) : null);
      if (matches) {
        const delay = step.clickAdvanceDelayMs ?? 150;
        if (delay >= 500) setIsFadingOut(true);
        window.setTimeout(() => next(), delay);
      }
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [active, step, next]);

  if (!active || !step) return null;

  // Welcome step: render Modal with typewriter and Skip / Show me! buttons.
  if (step.id === "welcome") {
    return (
      <Modal
        isOpen
        title="Welcome to Parallel!"
        confirmText="Show me!"
        cancelText="Skip"
        onConfirm={() => next()}
        onCancel={() => end(true)}
      >
        <Typewriter text={step.body} />
      </Modal>
    );
  }

  // Completion step: render Modal with a single End Walkthrough button.
  if (step.isCompletionModal) {
    return createPortal(
      <div className="modal-overlay" style={{ zIndex: 6000 }}>
        <div className="modal">
          <button
            type="button"
            className="modal-close"
            onClick={() => end(true)}
            aria-label="Close"
          >
            <span className="modal-cancel-text">Cancel (ESC)</span>
            <img src="/assets/x.svg" alt="" aria-hidden="true" className="modal-close-icon" />
          </button>
          <h2>You're all set!</h2>
          <div className="modal-content">
            <Typewriter text={step.body} />
          </div>
          <div className="modal-actions">
            <button className="button-primary" onClick={() => end(true)}>
              End Walkthrough
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const PADDING = step.highlightPadding ?? 8;
  const isCentered = !step.anchor || step.placement === "center";
  const holeBase = highlightRect || anchorRect;

  // Build the hole rect (used for both strip-based and spotlight rendering)
  const hole: Rect | null =
    !isCentered && holeBase && !step.noHighlight
      ? {
          top: holeBase.top - PADDING,
          left: holeBase.left - PADDING,
          width: holeBase.width + PADDING * 2,
          height: holeBase.height + PADDING * 2,
        }
      : null;

  const placement = step.placement || "bottom";

  let bubbleStyle: React.CSSProperties;
  if (isCentered || (!anchorRect && !bubbleAnchorRect)) {
    bubbleStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: BUBBLE_WIDTH,
    };
  } else {
    let top = 0;
    let left = 0;
    const bubbleW = BUBBLE_WIDTH;
    const estimatedH = 110;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Use bubbleAnchorRect if provided, otherwise fall back to anchorRect
    const ref = bubbleAnchorRect || anchorRect!;

    if (placement === "bottom") {
      top = ref.top + ref.height + BUBBLE_GAP;
      left = ref.left + ref.width / 2 - bubbleW / 2;
    } else if (placement === "top") {
      top = ref.top - estimatedH - BUBBLE_GAP;
      left = ref.left + ref.width / 2 - bubbleW / 2;
    } else if (placement === "left") {
      top = ref.top + ref.height / 2 - estimatedH / 2;
      left = ref.left - bubbleW - BUBBLE_GAP;
    } else if (placement === "right") {
      top = ref.top + ref.height / 2 - estimatedH / 2;
      left = ref.left + ref.width + BUBBLE_GAP;
    }

    left = Math.max(12, Math.min(viewportW - bubbleW - 12, left));
    top = Math.max(48, Math.min(viewportH - estimatedH - 12, top));

    // Apply optional user offsets
    left += step.bubbleOffsetX ?? 0;
    top += step.bubbleOffsetY ?? 0;

    bubbleStyle = { top, left, width: bubbleW };
  }

  // Pulsing dot on the edge of the anchor facing the bubble — with optional offsets
  let dotStyle: React.CSSProperties | null = null;
  if (!step.hideDot && anchorRect) {
    let cx = anchorRect.left + anchorRect.width / 2;
    let cy = anchorRect.top + anchorRect.height / 2;
    if (placement === "bottom") cy = anchorRect.top + anchorRect.height;
    else if (placement === "top") cy = anchorRect.top;
    else if (placement === "left") cx = anchorRect.left;
    else if (placement === "right") cx = anchorRect.left + anchorRect.width;
    // Apply optional user offsets
    cx += step.dotOffsetX ?? 0;
    cy += step.dotOffsetY ?? 0;
    dotStyle = { top: cy, left: cx };
  }

  // Determine highlight border-radius
  const highlightRadius = step.highlightRadius ?? 0;

  const content = (
    <div
      className={`walkthrough-root${isFadingOut ? " walkthrough-root--fading" : ""}`}
      aria-live="polite"
    >
      {/* Overlay: pill/rounded spotlight or strip-based or full dim */}
      {step.hideOverlay ? null : hole && typeof highlightRadius === "number" && highlightRadius > 0 ? (
        // Rounded spotlight using box-shadow inset technique
        <div
          className="walkthrough-spotlight"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            borderRadius: highlightRadius,
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      ) : hole ? (
        // Classic 4-strip rectangular cutout
        <>
          <div
            className="walkthrough-strip"
            style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) }}
          />
          <div
            className="walkthrough-strip"
            style={{
              top: hole.top + hole.height,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
          <div
            className="walkthrough-strip"
            style={{
              top: hole.top,
              left: 0,
              width: Math.max(0, hole.left),
              height: hole.height,
            }}
          />
          <div
            className="walkthrough-strip"
            style={{
              top: hole.top,
              left: hole.left + hole.width,
              right: 0,
              height: hole.height,
            }}
          />
        </>
      ) : (
        <div className="walkthrough-dim" />
      )}

      {dotStyle && <div key={`dot-${step.id}`} className="walkthrough-dot" style={dotStyle} />}

      <div
        key={`bubble-${step.id}`}
        className={`walkthrough-bubble walkthrough-bubble--${placement}`}
        style={bubbleStyle}
        role="dialog"
      >
        <div className="walkthrough-body">{step.body}</div>
        {step.showNextButton && (
          <button
            type="button"
            className="walkthrough-next-button"
            onClick={() => next()}
          >
            Next
          </button>
        )}
      </div>

      {isIdle && (
        <div className="walkthrough-idle-toast" onClick={() => next()}>
          Skip to next step?
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
};

export default WalkthroughOverlay;
